import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import type { TFormQuestion } from '@onesource/services';
import { EQuestionInputType, getQuestionInputType, isShortAnswerValueValid } from '@onesource/schemas';

interface ShortAnswerQuestionProps {
  question: TFormQuestion;
  textAnswer: string;
  onTextChange: (text: string) => void;
  disabled?: boolean;
}

const HTML_INPUT_TYPE: Record<EQuestionInputType, string> = {
  [EQuestionInputType.TEXT]: 'text',
  [EQuestionInputType.EMAIL]: 'email',
  [EQuestionInputType.URL]: 'url',
  [EQuestionInputType.NUMBER]: 'number',
  [EQuestionInputType.PHONE]: 'tel',
};

const INVALID_MESSAGE: Record<EQuestionInputType, string> = {
  [EQuestionInputType.TEXT]: '',
  [EQuestionInputType.EMAIL]: 'Enter a valid email address',
  [EQuestionInputType.URL]: 'Enter a valid URL (including https://)',
  [EQuestionInputType.NUMBER]: 'Enter a valid number',
  [EQuestionInputType.PHONE]: 'Enter a valid phone number',
};

export const ShortAnswerQuestion = ({
  question,
  textAnswer,
  onTextChange,
  disabled = false,
}: ShortAnswerQuestionProps) => {
  const inputType = getQuestionInputType(question.metadata);
  const isMultiline = inputType === EQuestionInputType.TEXT;
  const invalid = !isShortAnswerValueValid(inputType, textAnswer);

  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: '16px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        p: 1.5,
      }}
    >
      <TextField
        fullWidth
        multiline={isMultiline}
        {...(isMultiline ? { rows: 4 } : { type: HTML_INPUT_TYPE[inputType] })}
        value={textAnswer}
        onChange={e => onTextChange(e.target.value)}
        disabled={disabled}
        placeholder="Type your answer here.."
        variant="outlined"
        error={invalid}
        helperText={invalid ? INVALID_MESSAGE[inputType] : undefined}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '1rem',
            bgcolor: 'white',
            borderRadius: '12px',
            '& .MuiOutlinedInput-input': {
              padding: '10px 0px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff4d6d',
              borderWidth: '1.5px',
              borderRadius: '12px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff4d6d',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff4d6d',
              borderWidth: '2px',
            },
          },
        }}
      />
    </Box>
  );
};
